import styled from '@emotion/styled'
import { VideoMetadata } from '../../../../content/Video/components/common/UnstyledVideoMetadata'
import { colors } from '../../../../__styling/settings/colors'
import { fonts } from '../../../../__styling/settings/fonts'
import { metrics } from '../../../../__styling/settings/metrics'
import { zIndex } from '../../../../__styling/settings/z-index'
import { breakpoint } from '../../../../__styling/style-functions'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
import { hexToRGBA } from '../../../../__styling/style-mixins'
import { themedValue } from '../../../../__styling/themed-value'

export interface StyledTopBarProps {
    isVisible: boolean
    compactMode?: boolean
}

export const StyledTopBar = styled('div')<StyledTopBarProps>(
    {
        position: 'absolute',
        zIndex: zIndex.common.videoTopBar,
        width: '100%',

        padding: calcRem(10),
        transition: 'transform 100ms ease, opacity 100ms ease',
        pointerEvents: 'none',
    },
    ({ theme, compactMode }) => ({
        padding: themedValue(theme, {
            thewest: calcRem(10, 10, 10, 56),
            sevennews: compactMode
                ? 0
                : calcRem(10, 10, 10, metrics.sevennews.margins.md),
            fallback: `${calcRem(10)}`,
        }),
        fontFamily: themedValue(theme, {
            thewest: fonts.thewest.sansSerif,
            perthnow: fonts.perthnow.sansSerifCond,
            sevennews: fonts.sevennews.sansSerifCond,
            fallback: undefined,
        }),
        background: themedValue(theme, {
            perthnow: colors.black80,
            thewest: 'rgba(255,255,255,0.9)',
            sevennews: `linear-gradient(to top, ${hexToRGBA(
                colors.black,
                0,
            )}, ${colors.black35} 80%)`,
            fallback: undefined,
        }),
        color: themedValue(theme, {
            perthnow: colors.white,
            thewest: colors.black,
            sevennews: colors.white,
            fallback: undefined,
        }),
        height: themedValue(theme, {
            sevennews: `100%`,
            fallback: undefined,
        }),
    }),
    ({ isVisible, theme }) =>
        isVisible === false &&
        theme.kind !== 'sevennews' && {
            transition: 'transform 1000ms ease',
            transform: 'translateY(-100%)',
        },
    ({ isVisible, theme }) =>
        isVisible === false &&
        theme.kind === 'sevennews' && {
            transition: 'opacity 1000ms ease',
            opacity: 0,
        },
    {
        '& .video-js__logo': {
            width: calcRem(36),
            height: calcRem(33),
            fill: colors.black,
            position: 'absolute',
            top: calcRem(11),
            left: calcRem(11),
        },
    },
)

export const StyledInset = styled('div')(
    {
        lineHeight: 1,
    },
    ({ theme }) =>
        theme.kind === 'sevennews' && {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
)

export const StyledHeadingAndLogoContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
})

export const StyledHeading = styled('h3')(
    {
        fontSize: calcRem(16),
        overflow: 'hidden',
        margin: 0,
        marginBottom: calcRem(3),
        width: '100%',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.15,
    },
    ({ theme }) => [
        {
            fontSize: themedValue(theme, {
                sevennews: calcRem(17),
                perthnow: calcRem(18),
                fallback: calcRem(16),
            }),
        },
        theme.kind === 'sevennews' && {
            fontWeight: 500,
            color: colors.white,
            whiteSpace: 'normal',
            padding: calcRem(metrics.sevennews.margins.sm),
            width: 'auto',
            marginBottom: 0,
        },
        {
            [breakpoint('sm')]: {
                fontSize: themedValue(theme, {
                    perthnow: calcRem(28),
                    fallback: calcRem(16),
                }),
            },
        },
    ],
)

export const StyledVideoMetadata = styled(VideoMetadata)({
    fontSize: calcRem(12),
    overflow: 'hidden',
    margin: 0,
    width: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
})
